/**
 * <collection-tile-slider>
 *
 * Wraps the theme's FoxTheme.Carousel (Swiper wrapper) for a single-row
 * tile slider, bounded (not looping). Advances one tile per arrow click.
 *
 * Desktop: tall-rect arrow buttons sit BESIDE the tiles (inline, flex
 * siblings), disabling natively at the first/last position via Swiper's
 * .swiper-button-disabled state. A centered "x / y" indicator shows below.
 * Mobile: inline arrows hide; one tile per view with swipe, plus mini
 * prev/next buttons flanking the count in the indicator.
 *
 * The "x / y" count is driven off Swiper's snap positions (snapGrid) — the
 * number of distinct scroll stops — so it reads sensibly on desktop too
 * (e.g. 8 tiles at 4-per-view = 5 stops → "1 / 5" ... "5 / 5").
 *
 * API contract (FoxTheme.Carousel wrapping Swiper) confirmed from the
 * theme's existing lookbook-card-slider component: initialize the Carousel
 * on the inner .swiper element with { init: false }, bind events on
 * .slider, then call .slider.init() once listeners are attached.
 */
customElements.get('collection-tile-slider') ||
  customElements.define(
    'collection-tile-slider',
    class extends HTMLElement {
      connectedCallback() {
        this.swiperEl = this.querySelector('.swiper');
        if (!this.swiperEl) return;

        this.indicatorEl = this.querySelector('.collection-tile-slider__indicator');
        this.currentEl = this.querySelector('.collection-tile-slider__current-page');
        this.totalEl = this.querySelector('.collection-tile-slider__total-pages');
        this.miniPrevEl = this.querySelector('.collection-tile-slider__mini-btn--prev');
        this.miniNextEl = this.querySelector('.collection-tile-slider__mini-btn--next');

        // The theme defines FoxTheme.Carousel in its core bundle. This
        // deferred script can occasionally run before that bundle finishes,
        // so wait for it rather than throwing and leaving a dead slider.
        this.whenCarouselReady(() => this.initSlider());
      }

      disconnectedCallback() {
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy(true, true);
        }
      }

      whenCarouselReady(cb, tries) {
        tries = tries || 0;
        if (window.FoxTheme && window.FoxTheme.Carousel) {
          cb();
          return;
        }
        if (tries > 120) return; // ~2s ceiling, then give up quietly
        requestAnimationFrame(() => this.whenCarouselReady(cb, tries + 1));
      }

      initSlider() {
        const tileCount = parseInt(this.dataset.tileCount, 10) || 4;
        // How many tiles an arrow click advances on desktop. Opt-in per
        // section via data-slides-per-group: unset means step one tile at a
        // time (the original tile-slider behavior); set it to the tile count
        // to page a whole row off and a whole row on. It also drives Swiper's
        // snapGrid, so the "x / y" indicator counts pages rather than tiles.
        // Mobile is one-up, so the group there is always 1.
        const perGroup = parseInt(this.dataset.slidesPerGroup, 10) || 1;
        // Transition duration in ms. Sections that page a whole row at a
        // time cover several tiles' worth of distance per click, so they set
        // this higher via data-speed; unset keeps the original 500.
        const speed = parseInt(this.dataset.speed, 10) || 500;

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 0,
          // 750 matches the OTG mobile breakpoint (749/750 pair) used
          // in this section's CSS — keep both in sync if either changes.
          breakpoints: {
            750: { slidesPerView: tileCount, slidesPerGroup: perGroup },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-btn-next'),
            prevEl: this.querySelector('.swiper-btn-prev'),
          },
          watchOverflow: true,
          speed: speed,
          loop: false,
          threshold: 2,
        };

        this.sliderInstance = new window.FoxTheme.Carousel(this.swiperEl, this.sliderOptions);
        this.sliderInstance.init();

        const swiper = this.sliderInstance.slider;
        const update = this.updateIndicator.bind(this);
        swiper.on('afterInit', update);
        swiper.on('slideChange', update);
        swiper.on('resize', update);
        swiper.on('breakpoint', update);
        swiper.init();

        // Mini buttons (mobile) step the slider one tile at a time. The big
        // inline buttons are wired via Swiper's navigation config above.
        if (this.miniPrevEl) {
          this.miniPrevEl.addEventListener('click', () => swiper.slidePrev());
        }
        if (this.miniNextEl) {
          this.miniNextEl.addEventListener('click', () => swiper.slideNext());
        }

        // Belt-and-suspenders: slide widths come from CSS custom props that
        // may resolve a frame after init, so recompute once more next frame.
        requestAnimationFrame(() => this.updateIndicator(swiper));
      }

      updateIndicator(swiper) {
        if (!this.indicatorEl) return;

        // snapGrid.length = number of distinct scroll stops for the current
        // viewport's slidesPerView. Falls back to slide count if unavailable.
        const total =
          swiper.snapGrid && swiper.snapGrid.length
            ? swiper.snapGrid.length
            : swiper.slides.length;
        // snapIndex is the position within snapGrid — i.e. the page. Using
        // activeIndex here would report the leading SLIDE (page 2 of a 4-up
        // group reads as slide 5), which only happened to match back when
        // every section stepped one slide at a time.
        const snap = typeof swiper.snapIndex === 'number' ? swiper.snapIndex : swiper.activeIndex;
        const current = Math.min(total, (snap || 0) + 1);

        if (this.currentEl) this.currentEl.textContent = current;
        if (this.totalEl) this.totalEl.textContent = total;

        // Nothing to paginate — hide the indicator rather than show "1 / 1".
        this.indicatorEl.classList.toggle('is-hidden', total <= 1);
      }
    }
  );
